import { useEffect, useState } from 'react'
import * as api from '@/apis/aipass'
import { CustomerType } from '@/models/customer-manager/customer'
import { ReservationType } from '@/models/reservation'
import { useCheckinUpdatedListener } from '@/hooks/use-checkin-updated-listener'
import { useErrorHandler } from '@/hooks/use-error-handler'

type ReadMode = 'checkinRead' | 'stayRead' | 'checkoutRead'

/**
 * チェックイン / 滞在 / チェックアウト詳細で共通の、チェックイン情報の取得と顧客紐付け。
 * 3画面が同じ処理を別々に持っていたため、再取得の条件を揃えて直せるようここに集めている。
 */
export const useAdminCheckinDetail = (checkinId: string, readMode: ReadMode) => {
  const { errorHandler } = useErrorHandler()
  // fetchAdminCheckin のレスポンスが型定義されていないため any のまま受ける
  const [checkin, setCheckin] = useState<any>()
  const [reservations, setReservations] = useState<ReservationType[]>([])
  const [currentTabReservationId, setCurrentTabReservationId] = useState<string>()
  const [customerLink, setCustomerLink] = useState<CustomerType>()
  const [tags, setTags] = useState<string[]>([])
  const [suggestedCustomers, setSuggestedCustomers] = useState<CustomerType[]>([])
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isSavingCustomerLink, setIsSavingCustomerLink] = useState<boolean>(false)

  const fetchAdminCheckin = async () => {
    if (checkinId === 'redirect') return
    setIsFetching(true)
    try {
      // fetchAdminCheckin は失敗時に例外を投げず undefined を返す
      const res = await api.fetchAdminCheckin(checkinId)
      if (!res) return
      setCheckin(res.checkin)
      setReservations(res.reservations)
      // 再取得後も選択中のタブを維持し、消えた予約を選んでいた場合だけ先頭に戻す
      setCurrentTabReservationId(current =>
        current !== undefined && res.reservations.some(r => r.reservationId === current) ? current : res.reservations[0]?.reservationId,
      )
      setCustomerLink(res.customer)
      // 顧客にタグ情報が未作成だと tagInfo は null で返る
      setTags(res.customer?.tagInfo?.tags.map(tag => tag.id) ?? [])
    } finally {
      setIsFetching(false)
    }
  }

  useCheckinUpdatedListener(fetchAdminCheckin)

  // checkinId は ReservationDetailRoute の key に含まれ、変わると画面ごと作り直されるためマウント時1回だけ取得する
  useEffect(() => {
    fetchAdminCheckin()
  }, [])

  // 再取得のたびに checkin は別オブジェクトになるため、既読化は ID が決まったときだけにする
  const checkinRecordId: string | undefined = checkin?.id
  useEffect(() => {
    if (!checkinRecordId) return
    api.updateAdminCheckinList({ checkinId: checkinRecordId, mode: readMode })
  }, [checkinRecordId, readMode])

  // 顧客候補の検索も同じ理由で電話番号が変わったときだけにする
  const guestTelephone: string | undefined = checkin?.guest?.telephone
  useEffect(() => {
    if (!guestTelephone || guestTelephone === '0') {
      setSuggestedCustomers([])
      return
    }
    api
      .fetchCustomerSearch({ guestTelephone, page: 1, limit: 100 })
      .then(res => setSuggestedCustomers(res?.hotelCuicinUsers))
      .catch(e => console.error(e))
  }, [guestTelephone])

  /** 成功したかを返す。紐付けモーダルは成功したときだけ閉じるため */
  const linkCustomer = async (hotelCuicinUserId: string): Promise<boolean> => {
    setIsSavingCustomerLink(true)
    try {
      await api.createCustomerLink(checkin.id, '', hotelCuicinUserId)
      await fetchAdminCheckin()
      return true
    } catch (error) {
      errorHandler(error)
      return false
    } finally {
      setIsSavingCustomerLink(false)
    }
  }

  const unlinkCustomer = async () => {
    setIsSavingCustomerLink(true)
    try {
      await api.deleteCustomerLink(checkin.id, '')
      setCustomerLink(undefined)
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsSavingCustomerLink(false)
    }
  }

  return {
    checkin,
    reservations,
    currentTabReservationId,
    setCurrentTabReservationId,
    customerLink,
    tags,
    setTags,
    suggestedCustomers,
    isFetching,
    isSavingCustomerLink,
    fetchAdminCheckin,
    linkCustomer,
    unlinkCustomer,
  }
}
